import React, { useState, useEffect } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { TeamMetrics } from '@/components/workforce/TeamMetrics'
import { DirectReportsCard } from '@/components/workforce/DirectReportsCard'
import { HierarchyTreeView } from '@/components/workforce/HierarchyTreeView'
import { ReassignManagerModal } from '@/components/workforce/ReassignManagerModal'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { EmptyState } from '@/components/ui/EmptyState'
import {
  getMyManagementTree,
  getMyAllReports,
  updateEmployee,
  getEmployee,
  EmployeeTree
} from '@/api/workforce'
import { Employee } from '@/types/workforce'
import {
  Users,
  List,
  Network,
  Download,
  UserCircle,
  Building2,
  Mail,
  Phone,
  Briefcase,
  Calendar,
  X,
  RefreshCw
} from 'lucide-react'

type ViewMode = 'list' | 'tree'

export default function MyTeamPage() {
  const { user } = useAuth()
  const [viewMode, setViewMode] = useState<ViewMode>('list')
  const [directReports, setDirectReports] = useState<Employee[]>([])
  const [managementTree, setManagementTree] = useState<EmployeeTree | null>(null)
  const [allReports, setAllReports] = useState<Employee[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Modal states
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null)
  const [employeeToReassign, setEmployeeToReassign] = useState<Employee | null>(null)
  const [currentManagerForModal, setCurrentManagerForModal] = useState<Employee | undefined>(undefined)

  useEffect(() => {
    loadTeamData()
  }, [])

  const loadTeamData = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true)
      } else {
        setLoading(true)
      }
      setError(null)

      const [directReportsData, treeData, allReportsData] = await Promise.all([
        getMyAllReports({ limit: 1000 }), // Use all-reports for direct reports too
        getMyManagementTree({ includeManager: true }),
        getMyAllReports({ limit: 1000 })
      ])

      setDirectReports(directReportsData.items)
      setManagementTree(treeData)
      setAllReports(allReportsData.items)
    } catch (err) {
      console.error('❌ Error loading team data:', err)
      setError(err instanceof Error ? err.message : 'Erro ao carregar dados da equipe')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  const handleViewDetails = (employee: Employee | EmployeeTree) => {
    setSelectedEmployee(employee as Employee)
  }

  const handleReassign = async (employee: Employee) => {
    setEmployeeToReassign(employee)

    // Buscar o gestor atual se existir
    if (employee.employment.managerId) {
      try {
        const tenantDomain = user?.employee?.tenantId || 'monumenta'

        // Tentar encontrar o gestor nos subordinados primeiro
        let manager = allReports.find(r => r.employeeId === employee.employment.managerId)

        // Se não encontrou, buscar na API
        if (!manager) {
          manager = await getEmployee(tenantDomain, employee.employment.managerId)
        }

        setCurrentManagerForModal(manager)
      } catch (error) {
        console.error('Erro ao buscar gestor atual:', error)
        setCurrentManagerForModal(undefined)
      }
    } else {
      setCurrentManagerForModal(undefined)
    }
  }

  const handleReassignConfirm = async (newManagerId: string | null) => {
    if (!employeeToReassign) return

    try {
      const tenantDomain = user?.employee?.tenantId || 'monumenta'
      await updateEmployee(tenantDomain, employeeToReassign.employeeId, {
        employment: {
          ...employeeToReassign.employment,
          managerId: newManagerId
        }
      })

      // Reload data
      await loadTeamData(true)

      // Close modal
      setEmployeeToReassign(null)
    } catch (error) {
      console.error('Error reassigning manager:', error)
      throw error
    }
  }

  const formatDate = (date: string) => {
    if (!date) return 'Data não informada'

    const dateObj = new Date(date)
    if (isNaN(dateObj.getTime())) return 'Data inválida'

    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(dateObj)
  }

  const getStatusLabel = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'ACTIVE':
        return 'Ativo'
      case 'INACTIVE':
        return 'Inativo'
      case 'TERMINATED':
        return 'Demitido'
      case 'PENDING':
        return 'Pendente'
      default:
        return status
    }
  }

  // Calculate average tenure (in years)
  const calculateAvgTenure = (): number | undefined => {
    if (!allReports || allReports.length === 0) return undefined

    const totalDays = allReports.reduce((sum, emp) => {
      if (!emp.employment?.startDate) return sum

      const startDate = new Date(emp.employment.startDate)
      const today = new Date()

      // Validate date
      if (isNaN(startDate.getTime())) return sum

      const diffTime = Math.abs(today.getTime() - startDate.getTime())
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
      return sum + diffDays
    }, 0)

    const avgDays = totalDays / allReports.length
    const avgYears = avgDays / 365

    // Return undefined if calculation resulted in invalid number
    return isNaN(avgYears) ? undefined : avgYears
  }

  if (loading) {
    return (
      
        <div className="flex justify-center items-center min-h-[400px]">
          <div className="text-center">
            <LoadingSpinner size="lg" />
            <p className="text-brand-gray-light font-body mt-4">
              Carregando dados da equipe...
            </p>
          </div>
        </div>
      
    )
  }

  if (error) {
    return (
      
        <EmptyState
          icon={Users}
          title="Erro ao Carregar Equipe"
          description={error}
          action={
            <Button onClick={() => loadTeamData()}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Tentar Novamente
            </Button>
          }
        />
      
    )
  }

  return (
    <>
      <div className="space-y-6 animate-fade-in-up">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold text-white mb-2">
              Minha Equipe
            </h1>
            <p className="font-body text-brand-gray">
              Gerencie seus subordinados diretos e visualize sua hierarquia
            </p>
          </div>
          <div className="mt-4 sm:mt-0 flex space-x-3">
            {user && (user.role === 'admin' || user.role === 'rh' || user.role === 'analista-rh') && (
              <Button variant="ghost" size="sm">
                <Download className="w-4 h-4 mr-2" />
                Exportar
              </Button>
            )}
          </div>
        </div>

        {/* Team Metrics */}
        <TeamMetrics
          directReportsCount={directReports.length}
          totalTeamSize={allReports.length}
          spanOfControl={directReports.length}
          avgTenure={calculateAvgTenure()}
        />

        {/* View Mode Toggle */}
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-lg font-semibold text-white">
              {viewMode === 'list' ? 'Subordinados Diretos' : 'Hierarquia Completa'}
            </h2>
            <div className="flex gap-2">
              <Button
                variant={viewMode === 'list' ? 'primary' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
              >
                <List className="w-4 h-4 mr-2" />
                Lista
              </Button>
              <Button
                variant={viewMode === 'tree' ? 'primary' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('tree')}
              >
                <Network className="w-4 h-4 mr-2" />
                Hierarquia
              </Button>
            </div>
          </div>
        </Card>

        {/* Main Content */}
        {viewMode === 'list' ? (
          <DirectReportsCard
            reports={directReports}
            onViewDetails={handleViewDetails}
            onReassign={handleReassign}
          />
        ) : managementTree ? (
          <HierarchyTreeView
            managementTree={managementTree}
            onEmployeeClick={handleViewDetails}
          />
        ) : (
          <EmptyState
            icon={Network}
            title="Sem Dados de Hierarquia"
            description="Não foi possível carregar a árvore hierárquica."
          />
        )}
      </div>

      {/* Employee Detail Modal */}
      {selectedEmployee && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 animate-fade-in">
          <Card className="max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="font-display text-2xl font-bold text-white">
                  Detalhes do Colaborador
                </h2>
                <button
                  onClick={() => setSelectedEmployee(null)}
                  className="text-brand-gray hover:text-white transition-colors duration-200"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="space-y-6">
                {/* Employee Header */}
                <div className="flex items-center space-x-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-brand-primary to-brand-accent-600 rounded-full flex items-center justify-center shadow-monumenta-brand">
                    <UserCircle className="w-12 h-12 text-brand-dark" />
                  </div>
                  <div>
                    <h3 className="font-display text-xl font-bold text-white">
                      {selectedEmployee.personalInfo?.name}
                    </h3>
                    <p className="text-lg text-brand-gray">
                      {selectedEmployee.employment?.role?.name || 'Função não definida'}
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-success/20 text-success border border-success/40">
                        {getStatusLabel(selectedEmployee.employment?.status || 'ACTIVE')}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Contact Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center space-x-3">
                    <Mail className="w-5 h-5 text-brand-gray flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm text-brand-gray">Email</p>
                      <p className="font-medium text-white truncate">
                        {selectedEmployee.email}
                      </p>
                    </div>
                  </div>
                  {selectedEmployee.personalInfo?.phone && (
                    <div className="flex items-center space-x-3">
                      <Phone className="w-5 h-5 text-brand-gray flex-shrink-0" />
                      <div>
                        <p className="text-sm text-brand-gray">Telefone</p>
                        <p className="font-medium text-white">
                          {selectedEmployee.personalInfo.phone}
                        </p>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center space-x-3">
                    <Building2 className="w-5 h-5 text-brand-gray flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm text-brand-gray">Departamento</p>
                      <p className="font-medium text-white truncate">
                        {selectedEmployee.employment?.department?.name || 'N/A'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Briefcase className="w-5 h-5 text-brand-gray flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm text-brand-gray">Tipo de Contrato</p>
                      <p className="font-medium text-white">
                        {selectedEmployee.employment?.contractType || 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Employment Info */}
                <div>
                  <h4 className="font-display font-semibold text-white mb-3">
                    Informações de Emprego
                  </h4>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-brand-gray" />
                      <span className="text-brand-gray">Data de Admissão:</span>
                      <span className="text-white">
                        {formatDate(selectedEmployee.employment.startDate)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-4 border-t border-gray-700/50">
                  {user && (user.role === 'admin' || user.role === 'rh' || user.role === 'analista-rh') && (
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setEmployeeToReassign(selectedEmployee)
                        setSelectedEmployee(null)
                      }}
                      className="flex-1"
                    >
                      Reatribuir Gestor
                    </Button>
                  )}
                  <Button
                    variant="primary"
                    onClick={() => setSelectedEmployee(null)}
                    className="flex-1"
                  >
                    Fechar
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Reassign Manager Modal */}
      {employeeToReassign && (
        <ReassignManagerModal
          employee={employeeToReassign}
          currentManager={currentManagerForModal}
          onConfirm={handleReassignConfirm}
          onCancel={() => {
            setEmployeeToReassign(null)
            setCurrentManagerForModal(undefined)
          }}
        />
      )}
    </>
  )
}
